import React, { useMemo, useState } from 'react';
import './BeaconEditor.css';
import { beaconApi } from '../api/client';
import { BeaconModel } from '../types/beacon';
import { computeTarget, computeGap, computePriorityScore, defaultTarget } from '../utils/beaconScoring';

type EntityKey = 'domains' | 'capabilities' | 'subs' | 'apps' | 'inits';

const ENTITIES: Array<{ key: EntityKey; label: string; singular: string }> = [
  { key: 'domains', label: 'Domains', singular: 'Domain' },
  { key: 'capabilities', label: 'Capabilities', singular: 'Capability' },
  { key: 'subs', label: 'Sub-caps', singular: 'Sub-capability' },
  { key: 'apps', label: 'Applications', singular: 'Application' },
  { key: 'inits', label: 'Initiatives', singular: 'Initiative' },
];

const TIERS = ['Differentiating', 'Core', 'Commodity'];
const APP_STATUS = ['strategic', 'invest', 'tolerate', 'retire'];
const INIT_HORIZON = ['Now', 'Next', 'Later'];
const INIT_STATUS = ['Proposed', 'Approved', 'Scaling', 'In flight'];
const CONFIDENCE = ['High', 'Medium', 'Low'];

const API: Record<EntityKey, { create: (d: any) => Promise<any>; update: (id: string, d: any) => Promise<any>; del: (id: string) => Promise<any> }> = {
  domains: { create: beaconApi.createDomain, update: beaconApi.updateDomain, del: beaconApi.deleteDomain },
  capabilities: { create: beaconApi.createCapability, update: beaconApi.updateCapability, del: beaconApi.deleteCapability },
  subs: { create: beaconApi.createSubCapability, update: beaconApi.updateSubCapability, del: beaconApi.deleteSubCapability },
  apps: { create: beaconApi.createApplication, update: beaconApi.updateApplication, del: beaconApi.deleteApplication },
  inits: { create: beaconApi.createInitiative, update: beaconApi.updateInitiative, del: beaconApi.deleteInitiative },
};

interface Props {
  open: boolean;
  model: BeaconModel;
  onClose: () => void;
  onChanged: () => void;
}

// ---- Small field primitives (styled via BeaconEditor.css, tokens inherited from .beacon-app) ----
const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => (
  <label className="be-field">
    <span className="be-label">{label}{hint && <em> {hint}</em>}</span>
    {children}
  </label>
);

const Scale: React.FC<{ value: number; onChange: (n: number) => void }> = ({ value, onChange }) => (
  <div className="chip-toggles be-scale">
    {[1, 2, 3, 4, 5].map((n) => (
      <button type="button" key={n} className={value === n ? 'active' : ''} onClick={() => onChange(n)}>{n}</button>
    ))}
  </div>
);

const Segmented: React.FC<{ value: string; options: string[]; onChange: (v: string) => void }> = ({ value, options, onChange }) => (
  <div className="chip-toggles be-seg">
    {options.map((o) => (
      <button type="button" key={o} className={value === o ? 'active' : ''} onClick={() => onChange(o)}>{o}</button>
    ))}
  </div>
);

const MultiChip: React.FC<{ selected: string[]; options: Array<{ id: string; label: string; sub?: string }>; onChange: (ids: string[]) => void }> = ({ selected, options, onChange }) => (
  <div className="be-multi">
    {options.length === 0 && <div className="be-inline-empty">None available yet — add some first.</div>}
    {options.map((o) => {
      const on = selected.includes(o.id);
      return (
        <button type="button" key={o.id} className={`be-tag ${on ? 'on' : ''}`} title={o.sub} onClick={() => onChange(on ? selected.filter((x) => x !== o.id) : [...selected, o.id])}>
          {o.label}
        </button>
      );
    })}
  </div>
);

const BeaconEditor: React.FC<Props> = ({ open, model, onClose, onChanged }) => {
  const [entity, setEntity] = useState<EntityKey>('domains');
  const [form, setForm] = useState<any | null>(null); // null = list view
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const capsFull = useMemo(
    () => model.domains.flatMap((d) => d.capabilities.map((c) => ({ ...c, domainId: d.id, domainName: d.name }))),
    [model],
  );
  const subsFull = useMemo(
    () => model.domains.flatMap((d) => d.capabilities.flatMap((c) => c.subCapabilities.map((s) => ({ ...s, capId: c.id, capName: c.name, domainName: d.name })))),
    [model],
  );
  const capOptions = useMemo(() => capsFull.map((c) => ({ id: c.id, label: c.name, sub: c.domainName })), [capsFull]);
  const appOptions = useMemo(() => model.applications.map((a) => ({ id: a.id, label: a.name, sub: a.vendor || undefined })), [model]);
  const initOptions = useMemo(() => model.initiatives.map((i) => ({ id: i.id, label: i.name })), [model]);

  const counts: Record<EntityKey, number> = {
    domains: model.domains.length,
    capabilities: capsFull.length,
    subs: subsFull.length,
    apps: model.applications.length,
    inits: model.initiatives.length,
  };

  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

  const newForm = (e: EntityKey): any => {
    switch (e) {
      case 'domains': return { __id: null, name: '', description: '', owner: '', color: '#4292c6', sortOrder: model.domains.length };
      case 'capabilities': return { __id: null, domainId: model.domains[0]?.id || '', name: '', purpose: '', owner: '', tier: 'Core', maturity: 3, pain: 3, importance: 3, targetAuto: true, targetMaturity: 4, spend: 0, applicationIds: [], initiativeIds: [] };
      case 'subs': return { __id: null, capabilityId: capsFull[0]?.id || '', name: '', purpose: '', maturity: 3, pain: 3, importance: 3, targetAuto: true, targetMaturity: 4 };
      case 'apps': return { __id: null, name: '', vendor: '', type: '', cost: 0, status: 'invest', lifecycle: '', capabilityIds: [] };
      case 'inits': return { __id: null, name: '', horizon: 'Now', cost: 0, status: 'Proposed', confidence: 'Medium', owner: '', outcome: '', capabilityIds: [] };
    }
  };

  const editForm = (e: EntityKey, item: any): any => {
    switch (e) {
      case 'domains': return { __id: item.id, name: item.name, description: item.description || '', owner: item.owner || '', color: item.color || '#4292c6', sortOrder: item.sortOrder ?? 0 };
      case 'capabilities': {
        const auto = item.target === defaultTarget(item.maturity, item.importance);
        return { __id: item.id, domainId: item.domainId, name: item.name, purpose: item.purpose || '', owner: item.owner || '', tier: item.tier || 'Core', maturity: item.maturity, pain: item.pain, importance: item.importance, targetAuto: auto, targetMaturity: item.target, spend: item.spend, applicationIds: [...item.applicationIds], initiativeIds: [...item.initiativeIds] };
      }
      case 'subs': {
        const auto = item.target === defaultTarget(item.maturity, item.importance);
        return { __id: item.id, capabilityId: item.capId, name: item.name, purpose: item.purpose || '', maturity: item.maturity, pain: item.pain, importance: item.importance, targetAuto: auto, targetMaturity: item.target };
      }
      case 'apps': return { __id: item.id, name: item.name, vendor: item.vendor || '', type: item.type || '', cost: item.cost, status: item.status || 'invest', lifecycle: item.lifecycle || '', capabilityIds: [...item.capabilityIds] };
      case 'inits': return { __id: item.id, name: item.name, horizon: item.horizon || 'Now', cost: item.cost, status: item.status || 'Proposed', confidence: item.confidence || 'Medium', owner: item.owner || '', outcome: item.outcome || '', capabilityIds: [...item.capabilityIds] };
    }
  };

  const buildPayload = (e: EntityKey, f: any): any => {
    const num = (v: any) => (v === '' || v == null ? 0 : Number(v));
    switch (e) {
      case 'domains': return { name: f.name.trim(), description: f.description || null, owner: f.owner || null, color: f.color, sortOrder: num(f.sortOrder) };
      case 'capabilities': return { domainId: f.domainId, name: f.name.trim(), purpose: f.purpose || null, owner: f.owner || null, tier: f.tier, maturity: num(f.maturity), pain: num(f.pain), importance: num(f.importance), targetMaturity: f.targetAuto ? null : num(f.targetMaturity), spend: num(f.spend), applicationIds: f.applicationIds, initiativeIds: f.initiativeIds };
      case 'subs': return { capabilityId: f.capabilityId, name: f.name.trim(), purpose: f.purpose || null, maturity: num(f.maturity), pain: num(f.pain), importance: num(f.importance), targetMaturity: f.targetAuto ? null : num(f.targetMaturity) };
      case 'apps': return { name: f.name.trim(), vendor: f.vendor || null, type: f.type || null, cost: num(f.cost), status: f.status, lifecycle: f.lifecycle || null, capabilityIds: f.capabilityIds };
      case 'inits': return { name: f.name.trim(), horizon: f.horizon, cost: num(f.cost), status: f.status, confidence: f.confidence, owner: f.owner || null, outcome: f.outcome || null, capabilityIds: f.capabilityIds };
    }
  };

  const validate = (e: EntityKey, f: any): string | null => {
    if (!f.name?.trim()) return 'Name is required.';
    if (e === 'capabilities' && !f.domainId) return 'Pick a domain — create one first if none exist.';
    if (e === 'subs' && !f.capabilityId) return 'Pick a capability — create one first if none exist.';
    return null;
  };

  const startNew = () => { setError(null); setForm(newForm(entity)); };
  const startEdit = (item: any) => { setError(null); setForm(editForm(entity, item)); };
  const backToList = () => { setError(null); setForm(null); };
  const switchEntity = (e: EntityKey) => { setEntity(e); setForm(null); setError(null); };

  const save = async () => {
    const msg = validate(entity, form);
    if (msg) { setError(msg); return; }
    setSaving(true); setError(null);
    try {
      const payload = buildPayload(entity, form);
      if (form.__id) await API[entity].update(form.__id, payload);
      else await API[entity].create(payload);
      onChanged();
      setForm(null);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Save failed. Check the fields and try again.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item: any) => {
    const label = ENTITIES.find((x) => x.key === entity)?.singular || 'item';
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm(`Delete this ${label.toLowerCase()} "${item.name}"? This can't be undone.`)) return;
    try {
      await API[entity].del(item.id);
      onChanged();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Delete failed.');
    }
  };

  const singular = ENTITIES.find((x) => x.key === entity)?.singular || '';

  // ---- List rows per entity ----
  const renderList = () => {
    if (entity === 'domains') {
      return model.domains.map((d) => (
        <Row key={d.id} title={d.name} meta={`${d.capabilities.length} capabilities${d.owner ? ` · ${d.owner}` : ''}`} swatch={d.color} onEdit={() => startEdit(d)} onDelete={() => remove(d)} />
      ));
    }
    if (entity === 'capabilities') {
      return capsFull.map((c) => (
        <Row key={c.id} title={c.name} meta={`${c.domainName} · M${c.maturity} · Gap +${c.gap} · PS ${c.ps}`} tier={c.tier} onEdit={() => startEdit(c)} onDelete={() => remove(c)} />
      ));
    }
    if (entity === 'subs') {
      return subsFull.map((s) => (
        <Row key={s.id} title={s.name} meta={`${s.domainName} → ${s.capName} · M${s.maturity} · PS ${s.ps}`} onEdit={() => startEdit(s)} onDelete={() => remove(s)} />
      ));
    }
    if (entity === 'apps') {
      return model.applications.map((a) => (
        <Row key={a.id} title={a.name} meta={`${a.vendor || 'No vendor'} · $${a.cost}M/yr · ${a.capabilityIds.length} capabilities`} pill={a.status} onEdit={() => startEdit(a)} onDelete={() => remove(a)} />
      ));
    }
    return model.initiatives.map((i) => (
      <Row key={i.id} title={i.name} meta={`${i.horizon} · $${i.cost}M · ${i.status}`} pill={i.confidence} onEdit={() => startEdit(i)} onDelete={() => remove(i)} />
    ));
  };

  // ---- Forms ----
  const renderForm = () => {
    const f = form;
    const scoreInput = {
      maturity: Number(f.maturity), pain: Number(f.pain), importance: Number(f.importance),
      targetMaturity: f.targetAuto ? null : Number(f.targetMaturity),
    };
    const preview = (entity === 'capabilities' || entity === 'subs')
      ? { target: computeTarget(scoreInput), gap: computeGap(scoreInput), ps: computePriorityScore(scoreInput) }
      : null;

    return (
      <div className="be-form">
        {entity === 'domains' && (
          <>
            <Field label="Name"><input className="be-input" value={f.name} onChange={(e) => set('name', e.target.value)} autoFocus placeholder="e.g. Customer Operations" /></Field>
            <Field label="Description"><textarea className="be-input" rows={3} value={f.description} onChange={(e) => set('description', e.target.value)} placeholder="What this domain covers…" /></Field>
            <div className="be-grid-2">
              <Field label="Owner" hint="Name — Title"><input className="be-input" value={f.owner} onChange={(e) => set('owner', e.target.value)} placeholder="Dana Lee — VP Operations" /></Field>
              <Field label="Sort order"><input className="be-input" type="number" value={f.sortOrder} onChange={(e) => set('sortOrder', e.target.value)} /></Field>
            </div>
            <Field label="Color">
              <div className="be-color"><input type="color" value={f.color} onChange={(e) => set('color', e.target.value)} /><span>{f.color}</span></div>
            </Field>
          </>
        )}

        {entity === 'capabilities' && (
          <>
            <div className="be-grid-2">
              <Field label="Domain"><select className="be-input" value={f.domainId} onChange={(e) => set('domainId', e.target.value)}>{model.domains.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</select></Field>
              <Field label="Differentiation tier"><Segmented value={f.tier} options={TIERS} onChange={(v) => set('tier', v)} /></Field>
            </div>
            <Field label="Name"><input className="be-input" value={f.name} onChange={(e) => set('name', e.target.value)} autoFocus placeholder="e.g. Order Management" /></Field>
            <Field label="Purpose"><textarea className="be-input" rows={2} value={f.purpose} onChange={(e) => set('purpose', e.target.value)} /></Field>
            <div className="be-grid-2">
              <Field label="Owner" hint="Name — Title"><input className="be-input" value={f.owner} onChange={(e) => set('owner', e.target.value)} /></Field>
              <Field label="Annual spend" hint="$M"><input className="be-input" type="number" step="0.1" value={f.spend} onChange={(e) => set('spend', e.target.value)} /></Field>
            </div>
            <div className="be-scores">
              <Field label="Maturity" hint="1 ad hoc → 5 optimized"><Scale value={f.maturity} onChange={(n) => set('maturity', n)} /></Field>
              <Field label="Pain" hint="1 none → 5 severe"><Scale value={f.pain} onChange={(n) => set('pain', n)} /></Field>
              <Field label="Strategic importance" hint="1 hygiene → 5 mission"><Scale value={f.importance} onChange={(n) => set('importance', n)} /></Field>
            </div>
            <Field label="Target maturity">
              <div className="be-target">
                <button type="button" className={`be-toggle ${f.targetAuto ? 'on' : ''}`} onClick={() => set('targetAuto', true)}>Auto</button>
                <button type="button" className={`be-toggle ${!f.targetAuto ? 'on' : ''}`} onClick={() => set('targetAuto', false)}>Set manually</button>
                {!f.targetAuto && <Scale value={f.targetMaturity} onChange={(n) => set('targetMaturity', n)} />}
              </div>
            </Field>
            {renderRelations(f)}
          </>
        )}

        {entity === 'subs' && (
          <>
            <Field label="Capability"><select className="be-input" value={f.capabilityId} onChange={(e) => set('capabilityId', e.target.value)}>{capsFull.map((c) => <option key={c.id} value={c.id}>{c.domainName} → {c.name}</option>)}</select></Field>
            <Field label="Name"><input className="be-input" value={f.name} onChange={(e) => set('name', e.target.value)} autoFocus /></Field>
            <Field label="Purpose"><textarea className="be-input" rows={2} value={f.purpose} onChange={(e) => set('purpose', e.target.value)} /></Field>
            <div className="be-scores">
              <Field label="Maturity"><Scale value={f.maturity} onChange={(n) => set('maturity', n)} /></Field>
              <Field label="Pain"><Scale value={f.pain} onChange={(n) => set('pain', n)} /></Field>
              <Field label="Importance"><Scale value={f.importance} onChange={(n) => set('importance', n)} /></Field>
            </div>
            <Field label="Target maturity">
              <div className="be-target">
                <button type="button" className={`be-toggle ${f.targetAuto ? 'on' : ''}`} onClick={() => set('targetAuto', true)}>Auto</button>
                <button type="button" className={`be-toggle ${!f.targetAuto ? 'on' : ''}`} onClick={() => set('targetAuto', false)}>Set manually</button>
                {!f.targetAuto && <Scale value={f.targetMaturity} onChange={(n) => set('targetMaturity', n)} />}
              </div>
            </Field>
          </>
        )}

        {entity === 'apps' && (
          <>
            <Field label="Name"><input className="be-input" value={f.name} onChange={(e) => set('name', e.target.value)} autoFocus placeholder="e.g. Salesforce" /></Field>
            <div className="be-grid-2">
              <Field label="Vendor"><input className="be-input" value={f.vendor} onChange={(e) => set('vendor', e.target.value)} /></Field>
              <Field label="Type" hint="e.g. SaaS, Custom"><input className="be-input" value={f.type} onChange={(e) => set('type', e.target.value)} /></Field>
            </div>
            <div className="be-grid-2">
              <Field label="Annual cost" hint="$M"><input className="be-input" type="number" step="0.1" value={f.cost} onChange={(e) => set('cost', e.target.value)} /></Field>
              <Field label="Lifecycle"><input className="be-input" value={f.lifecycle} onChange={(e) => set('lifecycle', e.target.value)} placeholder="e.g. Production" /></Field>
            </div>
            <Field label="TIME status"><Segmented value={f.status} options={APP_STATUS} onChange={(v) => set('status', v)} /></Field>
            <Field label="Supports capabilities"><MultiChip selected={f.capabilityIds} options={capOptions} onChange={(ids) => set('capabilityIds', ids)} /></Field>
          </>
        )}

        {entity === 'inits' && (
          <>
            <Field label="Name"><input className="be-input" value={f.name} onChange={(e) => set('name', e.target.value)} autoFocus placeholder="e.g. Billing platform replatform" /></Field>
            <div className="be-grid-2">
              <Field label="Horizon"><Segmented value={f.horizon} options={INIT_HORIZON} onChange={(v) => set('horizon', v)} /></Field>
              <Field label="Investment" hint="$M"><input className="be-input" type="number" step="0.1" value={f.cost} onChange={(e) => set('cost', e.target.value)} /></Field>
            </div>
            <div className="be-grid-2">
              <Field label="Status"><Segmented value={f.status} options={INIT_STATUS} onChange={(v) => set('status', v)} /></Field>
              <Field label="Confidence"><Segmented value={f.confidence} options={CONFIDENCE} onChange={(v) => set('confidence', v)} /></Field>
            </div>
            <Field label="Owner"><input className="be-input" value={f.owner} onChange={(e) => set('owner', e.target.value)} /></Field>
            <Field label="Target outcome"><textarea className="be-input" rows={2} value={f.outcome} onChange={(e) => set('outcome', e.target.value)} /></Field>
            <Field label="Advances capabilities"><MultiChip selected={f.capabilityIds} options={capOptions} onChange={(ids) => set('capabilityIds', ids)} /></Field>
          </>
        )}

        {preview && (
          <div className="be-preview">
            <span className="be-preview-label">Derived</span>
            <span className="score-pill"><span className="lbl">Target</span> M{preview.target}</span>
            <span className="score-pill"><span className="lbl">Gap</span> {preview.gap === 0 ? 'At target' : `+${preview.gap}`}</span>
            <span className="score-pill"><span className="lbl">Priority</span> {preview.ps}</span>
          </div>
        )}
      </div>
    );
  };

  const renderRelations = (f: any) => (
    <>
      <Field label="Supporting applications"><MultiChip selected={f.applicationIds} options={appOptions} onChange={(ids) => set('applicationIds', ids)} /></Field>
      <Field label="Advancing initiatives"><MultiChip selected={f.initiativeIds} options={initOptions} onChange={(ids) => set('initiativeIds', ids)} /></Field>
    </>
  );

  return (
    <>
      <div className={`detail-backdrop ${open ? 'open' : ''}`} onClick={onClose} />
      <aside className={`detail-panel be-panel ${open ? 'open' : ''}`}>
        <div className="be-head">
          <button className="dp-close" aria-label="Close" onClick={onClose}>×</button>
          <div className="be-title-row">
            <div>
              <div className="crumb">Manage Beacon data</div>
              <h2>{form ? (form.__id ? `Edit ${singular}` : `New ${singular}`) : 'Data'}</h2>
            </div>
          </div>
          <div className="be-tabs chip-toggles">
            {ENTITIES.map((e) => (
              <button key={e.key} type="button" className={entity === e.key ? 'active' : ''} onClick={() => switchEntity(e.key)}>
                {e.label}<span className="be-count">{counts[e.key]}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="be-body">
          {error && <div className="be-error">{error}</div>}

          {form ? (
            <>
              {renderForm()}
              <div className="be-actions">
                <button className="btn" onClick={backToList} disabled={saving}>Cancel</button>
                <button className="btn active" onClick={save} disabled={saving}>{saving ? 'Saving…' : (form.__id ? 'Save changes' : `Add ${singular.toLowerCase()}`)}</button>
              </div>
            </>
          ) : (
            <>
              <div className="be-list-head">
                <span>{counts[entity]} {counts[entity] === 1 ? singular.toLowerCase() : `${singular.toLowerCase()}s`}</span>
                <button className="btn active" onClick={startNew}>＋ New {singular.toLowerCase()}</button>
              </div>
              <div className="be-list">
                {counts[entity] === 0 && <div className="be-empty">No {singular.toLowerCase()}s yet. Add the first one.</div>}
                {renderList()}
              </div>
            </>
          )}
        </div>
      </aside>
    </>
  );
};

// ---- List row ----
const Row: React.FC<{ title: string; meta: string; swatch?: string; tier?: string; pill?: string; onEdit: () => void; onDelete: () => void }> = ({ title, meta, swatch, tier, pill, onEdit, onDelete }) => (
  <div className="be-row" onClick={onEdit}>
    {swatch && <span className="be-swatch" style={{ background: swatch }} />}
    <div className="be-row-main">
      <div className="be-row-title">{title}</div>
      <div className="be-row-meta">{meta}</div>
    </div>
    {tier && <span className={`tier-chip tier-${tier}`}>{tier[0]}</span>}
    {pill && <span className="be-row-pill">{pill}</span>}
    <button className="be-row-del" title="Delete" onClick={(e) => { e.stopPropagation(); onDelete(); }}>Delete</button>
  </div>
);

export default BeaconEditor;
